import React, { useRef, useEffect } from "react";
import Paper from "@mui/material/Paper";
import ActionButton from "../account/ActionButton";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { PaymentConfirmationDisplay } from "../payment/PaymentConfirmation";
import {
  FormPaymentRequest,
  GlobalpayPayment3DSRequiredResponse,
  GlobalpayPaymentResponse,
} from "./types";
import environment from "../../env";

interface ConfirmationStepProps {
  readonly form: FormPaymentRequest;
  readonly onBack: () => void;
  readonly onSubmit: () => void;
  readonly submitting: boolean;
}

export function ConfirmationStep({
  form,
  onBack,
  onSubmit,
  submitting,
}: ConfirmationStepProps) {
  const formattedAmount = new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(Number(form.amount));

  const formattedCardNumber = form.cardNumber
    ? `${"*".repeat(form.cardNumber.length - 4)}${form.cardNumber.slice(-4)}`
    : "";

  return (
    <Paper sx={{ p: 3, mb: 2 }}>
      <Typography variant='h6' gutterBottom>
        Confirmation
      </Typography>
      <Typography>Amount: {formattedAmount}</Typography>
      <Typography>Name: {form.name}</Typography>
      <Typography>
        Card Number: {formattedCardNumber}
      </Typography>
      <Typography>
        Address: {form.addressLine1}, {form.postcode}
      </Typography>
      <Box display='flex' gap={2} mt={2}>
        <ActionButton
          id='remind-me-later-btn'
          borderColor='primary.dark'
          buttonColor='transparent'
          buttonTextColor='primary.dark'
          buttonTitle='Back'
          variant='outlined'
          hoverColor='primary.dark'
          disabled={submitting}
          hoverBorderColor={"primary.dark"}
          hoverTextColor='secondary.contrastText'
          onClick={onBack}
        />
        <ActionButton
          id='start-btn'
          variant='contained'
          buttonColor='secondary.main'
          buttonTextColor='primary.contrastText'
          borderColor='transparent'
          hoverColor='primary.contrastText'
          hoverBorderColor='secondary.main'
          hoverTextColor='secondary.main'
          buttonTitle={submitting ? "Processing..." : "Confirm Payment"}
          onClick={onSubmit}
          disabled={submitting}
        />
      </Box>
    </Paper>
  );
}

type Threedsv2ChallengeStepProps = {
  readonly challengeRequiredResponse: GlobalpayPayment3DSRequiredResponse;
  readonly onChallengeComplete: (cres: string) => void;
};

export function Threedsv2ChallengeStep({
  challengeRequiredResponse,
  onChallengeComplete,
}: Threedsv2ChallengeStepProps) {
  return (
    <Paper sx={{ p: 3, mb: 2 }}>
      <Typography variant="h6" gutterBottom>
        3DSv2 Challenge
      </Typography>
      <ExtendedAuthorisationStep
        acsUrl={challengeRequiredResponse.acsUrl}
        creq={challengeRequiredResponse.cres}
        onComplete={onChallengeComplete}
      />
    </Paper>
  );
}

type ResponseStepProps = {
  readonly response: GlobalpayPaymentResponse | null;
  readonly amount: number | string;
  readonly accountReference: string;
  readonly onComplete: () => void;
  readonly onTryAgain?: () => void;
};

export function ResponseStep({
  response,
  amount,
  accountReference,
  onComplete,
  onTryAgain,
}: ResponseStepProps) {
  return (
    <PaymentConfirmationDisplay
      accountReference={accountReference}
      amount={amount}
      paymentSuccess={response?.status === "Successful"}
      paymentId={response?.transactionId}
      isLoading={false}
      isPending={false}
      onComplete={onComplete}
      onTryAgain={onTryAgain}
    />
  );
}

type ExtendedAuthorisationStepProps = {
  readonly acsUrl: string;
  readonly creq: string;
  readonly onComplete?: (cres: string) => void;
};

export function ExtendedAuthorisationStep({
  acsUrl,
  creq,
  onComplete,
}: ExtendedAuthorisationStepProps) {
  // Reference to the iframe where the 3DSv2 challenge will be displayed
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  useEffect(() => {
    const expectedOrigin = environment.CUSTOMER_PORTAL_API.replace(
      /\/v.*$/,
      ""
    );
    const handleMessage = (e: MessageEvent<string>) => {
      if (e.origin !== expectedOrigin) {
        return;
      }
      if (e.data) {
        if (onComplete) onComplete(e.data);
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [onComplete]);

  // Create and submit the form - POSTing into the iframe
  useEffect(() => {
    if (!acsUrl || !creq) return;
    const iframe = iframeRef.current;
    if (!iframe) return;
    // Clean up any existing form
    const oldForm = document.getElementById("acs-form");
    if (oldForm) oldForm.remove();
    // Create a new form
    const form = document.createElement("form");
    form.method = "POST";
    form.action = acsUrl;
    form.target = "acs-iframe";
    form.id = "acs-form";
    const creqInput = document.createElement("input");
    creqInput.type = "hidden";
    creqInput.name = "creq";
    creqInput.value = creq;
    form.appendChild(creqInput);
    // Append the form to the body and submit it
    document.body.appendChild(form);
    form.submit();
    return () => {
      form.remove();
    };
  }, [acsUrl, creq]);

  return (
    <div className="mid clearfix">
      <div className="container">
        <iframe
          name="acs-iframe"
          ref={iframeRef}
          title="3DS Authentication"
          style={{
            width: "100%",
            height: 600,
            border: "none",
            overflow: "hidden",
          }}
        />
      </div>
    </div>
  );
}
